import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { roomAPI, branchAPI, pmsAPI } from '../../services/api';
import { useBranchStore, useAuthStore } from '../../store';
import { toast } from 'react-toastify';
import DateInput from '../../components/DateInput/DateInput';
import { formatDate, formatDateTime, formatTime, toInputDate } from '../../utils/dateFormat';
import './Rooms.css';

// Occupied is red and available green across the page (card title bar,
// pills, stat card) — so maintenance is violet, not a second red.
const STATUS_CONFIG = {
    available:   { label: 'Available',   color: '#16a34a', bg: '#dcfce7', icon: 'fa-check-circle' },
    occupied:    { label: 'Occupied',    color: '#dc2626', bg: '#fee2e2', icon: 'fa-user' },
    cleaning:    { label: 'Cleaning',    color: '#f59e0b', bg: '#fffbeb', icon: 'fa-broom' },
    maintenance: { label: 'Maintenance', color: '#7c3aed', bg: '#f5f3ff', icon: 'fa-tools' },
    blocked:     { label: 'Blocked',     color: '#6b7280', bg: '#f9fafb', icon: 'fa-ban' },
};

// Check-in date and time. A PMS that sends only the date is stored as the
// start of that day — "12:00 am" would be made up, so that shows the date.
function formatCheckIn(value) {
    const time = formatTime(value);
    if (time === '—') return '—';
    return /^12:00:00\s*am$/i.test(time) ? formatDate(value) : formatDateTime(value);
}

const ID_DOCUMENT_LABELS = { passport: 'Passport', aadhaar: 'Aadhaar', driving_license: 'Driving licence' };

const ROOM_TYPES   = ['standard', 'deluxe', 'suite', 'presidential', 'executive', 'villa', 'other'];
const AMENITY_ICONS = { wifi: 'fa-wifi', tv: 'fa-tv', minibar: 'fa-wine-bottle', safe: 'fa-lock', balcony: 'fa-archway', kitchenette: 'fa-utensils', bathtub: 'fa-bath', shower: 'fa-shower', power_monitor: 'fa-bolt' };
// "power_monitor" → "Power Monitor", "tv" → "Tv"
const amenityLabel = (a) => a.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

const AUDIT_ACTION_LABELS = {
    'room.create': 'Room created',
    'room.update': 'Room updated',
    'room.delete': 'Room deleted',
    'room.check_in': 'Guest checked in',
    'room.check_out': 'Guest checked out',
    'room.extend_stay': 'Stay extended',
};
const AUDIT_ACTION_ICONS = {
    'room.create': 'fa-plus-circle',
    'room.update': 'fa-pen',
    'room.delete': 'fa-trash',
    'room.check_in': 'fa-sign-in-alt',
    'room.check_out': 'fa-sign-out-alt',
    'room.extend_stay': 'fa-clock',
};

// PMS room events (backend RoomEventLog, written by roomStay.service.js).
const PMS_EVENT_ICONS = {
    checkin: 'fa-sign-in-alt',
    guest_change: 'fa-user-edit',
    room_shift: 'fa-exchange-alt',
    checkout: 'fa-sign-out-alt',
};
const PMS_FIELD_LABELS = {
    title: 'Title', name: 'Name', mobile: 'Mobile', email: 'Email', bookingId: 'Reservation',
    registrationNumber: 'Reg. card', vip: 'VIP', sharer: 'Sharer',
    checkInDate: 'Check-in', checkOutDate: 'Check-out', pmsAttributes: 'Attributes',
};

// One-line summary of a PMS room event, from the point of view of `roomNumber`
// (a shift shows as "moved in" on the new room and "moved out" on the old one).
function describePmsEvent(ev, roomNumber) {
    if (ev.type === 'room_shift') {
        const n = ev.sessionsMigrated || 0;
        const kept = n ? ` — ${n} active session${n === 1 ? '' : 's'} kept connected` : '';
        return ev.fromRoom === roomNumber
            ? `Guest moved to room ${ev.toRoom}${kept}`
            : `Guest moved in from room ${ev.fromRoom}${kept}${ev.details?.sharer ? ', sharing the room' : ''}`;
    }
    if (ev.type === 'guest_change') return 'Guest details changed';
    if (ev.type === 'checkout') {
        const why = ev.details?.resync ? ' (no longer listed by the PMS)' : '';
        if (!ev.details?.roomStaysOccupied) return `Guest checked out${why}`;
        return `Guest checked out${why} — the room stays occupied${ev.details.nowRoomGuest ? `, ${ev.details.nowRoomGuest} is now its guest` : ''}`;
    }
    if (ev.details?.sharer) return `Sharer checked in${ev.details.sharesWith ? ` (with ${ev.details.sharesWith})` : ''}`;
    return ev.details?.replacedGuest ? `Guest checked in (replaced ${ev.details.replacedGuest})` : 'Guest checked in';
}

function formatPmsValue(key, value) {
    if (value === null || value === undefined || value === '') return '—';
    if (key === 'checkInDate' || key === 'checkOutDate') return formatDateTime(value);
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'object') return Object.entries(value).map(([k, v]) => `${k}=${v}`).join(', ');
    return String(value);
}

// "Name: Avinash Patil → Avinash Rao" lines for a guest_change event.
function pmsChangeLines(details = {}) {
    return Object.entries(details).map(([key, change]) =>
        `${PMS_FIELD_LABELS[key] || key}: ${formatPmsValue(key, change?.from)} → ${formatPmsValue(key, change?.to)}`);
}

const emptyRoom  = { roomNumber: '', roomName: '', roomType: 'standard', floor: '', building: '', status: 'available', baseRate: '', notes: '', amenities: [], limits: { maxDevices: 3 }, capacity: { maxPax: '' } };
const emptyGuest = { name: '', email: '', mobile: '', checkInDate: '', checkOutDate: '', bookingId: '', registrationNumber: '', additionalGuests: [], adults: 1, children: 0 };
const emptyExtend = { newCheckOutDate: '', reason: '' };

// PMS-vs-manual source badge for a room — computed entirely from existing
// fields (no new locking mechanic): room.pms.syncStatus tracks "has this
// room ever been PMS-synced" (set by both the CapitaLand session-sync path
// and the WinCloud/WinHMS/IDS Next Module Mapping sync — see
// moduleRouting.service.js), while statusSource/sourceSystem describe who
// wrote the CURRENT occupancy. A room with PMS history whose current
// occupant was checked in manually is a "PMS + OVERRIDE" case.
function getSourceBadge(room) {
    const everPmsSynced = room.pms?.syncStatus === 'synced';
    if (room.statusSource === 'pms_auto' && room.sourceSystem && room.sourceSystem !== 'manual') {
        return { label: room.sourceSystem.toUpperCase(), className: 'src-badge-pms', title: `Occupancy synced from ${room.sourceSystem}` };
    }
    if (room.statusSource === 'manual' && everPmsSynced) {
        return { label: 'PMS + OVERRIDE', className: 'src-badge-override', title: 'This room has PMS sync history, but the current guest was checked in manually' };
    }
    if (room.status === 'occupied' && room.statusSource === 'manual') {
        return { label: 'MANUAL', className: 'src-badge-manual', title: 'Manually checked in — no PMS integration for this room' };
    }
    return null;
}

export default function Rooms() {
    const { selectedBranch } = useBranchStore();
    const admin = useAuthStore(s => s.admin);

    const [rooms, setRooms]         = useState([]);
    const [stats, setStats]         = useState(null);
    const [loading, setLoading]     = useState(true);
    const [filterStatus, setFilter] = useState('all');
    const [searchTerm, setSearch]   = useState('');
    const [viewMode, setViewMode]   = useState('grid');

    // PMS mode state
    const [pmsEnabled, setPmsEnabled]   = useState(false);
    const [syncing, setSyncing]         = useState(false);
    const [lastSync, setLastSync]       = useState(null);
    const autoRefreshRef                = useRef(null);
    const csvInputRef                   = useRef(null);
    // CSV import: format first, then the file's rows checked, then the import.
    const [showImportModal, setShowImportModal] = useState(false);
    const [importFile,      setImportFile]      = useState(null);   // { name, rows: [{ data, errors }] }
    const [importing,       setImporting]       = useState(false);
    const [importResult,    setImportResult]    = useState(null);   // { created, errors, errorDetails }

    // Modals
    const [showRoomModal,     setShowRoomModal]    = useState(false);
    const [showCheckInModal,  setShowCheckIn]      = useState(false);
    const [showEditGuestModal, setShowEditGuest]   = useState(false);
    const [showCheckOutModal, setShowCheckOut]     = useState(false);
    const [showDeleteModal,   setShowDelete]       = useState(false);
    const [showExtendModal,   setShowExtend]       = useState(false);
    const [showAuditModal,    setShowAudit]        = useState(false);
    const [showSearchModal,   setShowSearch]       = useState(false);

    const [editingRoom,  setEditingRoom]  = useState(null);
    const [roomForm,     setRoomForm]     = useState(emptyRoom);
    const [guestForm,    setGuestForm]    = useState(emptyGuest);
    const [editGuestForm, setEditGuestForm] = useState(emptyGuest);
    const [extendForm,   setExtendForm]   = useState(emptyExtend);
    const [selectedRoom, setSelectedRoom] = useState(null);
    const [saving,       setSaving]       = useState(false);
    // Guest details popup — by id, so the 30 s refresh keeps it current.
    const [guestRoomId,  setGuestRoomId]  = useState(null);

    // Audit log panel
    const [auditLogs,        setAuditLogs]        = useState([]);
    const [pmsEvents,        setPmsEvents]        = useState([]);
    const [loadingAuditLog,  setLoadingAuditLog]   = useState(false);

    // Global guest search
    const [guestSearchQuery,   setGuestSearchQuery]   = useState('');
    const [guestSearchResults, setGuestSearchResults] = useState([]);
    const [searchingGuests,    setSearchingGuests]    = useState(false);

    const branchId = selectedBranch?._id;

    // ── Detect PMS mode from branch config + seed lastSync ───────────────────
    useEffect(() => {
        if (!selectedBranch) return;
        const integrations = selectedBranch.integrations;
        const provider = integrations?.pms?.provider;

        const isCapitaland = integrations?.pms?.enabled && provider === 'capitaland' && integrations?.capitaland?.clientId;
        const isWincloud   = integrations?.pms?.enabled && provider === 'wincloud'   && integrations?.wincloud?.serverUrl;
        const isWinhms     = integrations?.pms?.enabled && provider === 'winhms'     && integrations?.winhms?.serverUrl;
        const isIdsnext    = integrations?.pms?.enabled && provider === 'idsnext'    && integrations?.idsnext?.serverUrl;
        const isHotla      = integrations?.pms?.enabled && provider === 'hotla'      && integrations?.hotla?.propNm;
        const isHotelkey   = integrations?.pms?.enabled && provider === 'hotelkey'   && integrations?.hotelkey?.appId;
        const isInnkey     = integrations?.pms?.enabled && provider === 'innkey'     && integrations?.innkey?.serverUrl;

        setPmsEnabled(!!(isCapitaland || isWincloud || isWinhms || isIdsnext || isHotla || isHotelkey || isInnkey));

        // Seed lastSync from branch data so it shows on first render
        const providerIntegration = integrations?.[provider];
        if (providerIntegration?.lastSyncAt) {
            setLastSync(new Date(providerIntegration.lastSyncAt));
        } else {
            setLastSync(null);
        }
    }, [selectedBranch]);

    // ── Fetch rooms + stats ───────────────────────────────────────────────────
    // `silent` = background refresh: keep the current grid on screen (no
    // full-page spinner) and don't toast on a transient failure.
    const fetchAll = useCallback(async ({ silent = false } = {}) => {
        if (!branchId) { setLoading(false); return; }
        if (!silent) setLoading(true);
        try {
            const [roomsRes, statsRes, branchRes] = await Promise.all([
                roomAPI.getAll({ branchId, status: filterStatus === 'all' ? undefined : filterStatus }),
                roomAPI.getStats(branchId),
                branchAPI.getById(branchId),
            ]);
            setRooms(roomsRes.data.data?.rooms || roomsRes.data.data || []);
            setStats(statsRes.data.data || null);
            // Refresh lastSync from updated branch data
            const branch = branchRes.data.data?.branch;
            if (branch) {
                const provider = branch.integrations?.pms?.provider;
                const lastSyncAt = branch.integrations?.[provider]?.lastSyncAt;
                if (lastSyncAt) setLastSync(new Date(lastSyncAt));
            }
        } catch {
            if (!silent) toast.error('Failed to load rooms');
        } finally {
            setLoading(false);
        }
    }, [branchId, filterStatus]);

    useEffect(() => { fetchAll(); }, [fetchAll]);

    // ── Auto-refresh every 30s in PMS mode ───────────────────────────────────
    useEffect(() => {
        if (pmsEnabled && branchId) {
            autoRefreshRef.current = setInterval(() => fetchAll({ silent: true }), 30000);
        }
        return () => clearInterval(autoRefreshRef.current);
    }, [pmsEnabled, branchId, fetchAll]);

    // ── PMS provider display name helper ─────────────────────────────────────
    const pmsProviderName = (() => {
        const p = selectedBranch?.integrations?.pms?.provider;
        if (p === 'wincloud') return 'WinCloud PMS';
        if (p === 'winhms')   return 'WinHMS';
        if (p === 'idsnext')  return 'IDS Next';
        if (p === 'hotla')    return 'HOTLA';
        if (p === 'hotelkey') return 'HotelKey';
        if (p === 'innkey')   return 'InnKey';
        return 'CapitaLand ODX';
    })();

    // ── PMS manual sync ───────────────────────────────────────────────────────
    const handlePmsSync = async () => {
        if (!branchId) return;
        setSyncing(true);
        try {
            const provider = selectedBranch?.integrations?.pms?.provider;

            if (provider === 'wincloud') {
                const res = await pmsAPI.syncWincloud(branchId);
                const d = res.data.data || {};
                setLastSync(new Date());
                toast.success(
                    `WinCloud sync complete — ${d.checkedIn || 0} new, ${d.updated || 0} updated, ${d.checkedOut || 0} checked out`
                );
            } else if (provider === 'winhms') {
                const res = await pmsAPI.syncWinhms(branchId);
                const d = res.data.data || {};
                setLastSync(new Date());
                toast.success(
                    `WinHMS sync complete — ${d.checkedIn || 0} new, ${d.updated || 0} updated, ${d.checkedOut || 0} checked out`
                );
            } else if (provider === 'idsnext') {
                const res = await pmsAPI.syncIdsnext(branchId);
                const d = res.data.data || {};
                setLastSync(new Date());
                toast.success(
                    `IDS Next sync complete — ${d.checkedIn || 0} new, ${d.updated || 0} updated, ${d.checkedOut || 0} checked out`
                );
            } else if (provider === 'hotla') {
                const res = await pmsAPI.syncHotla(branchId);
                const d = res.data.data || {};
                setLastSync(new Date());
                toast.success(
                    `HOTLA sync complete — ${d.checkedIn || 0} new, ${d.updated || 0} updated, ${d.shifted || 0} room shift(s), ${d.checkedOut || 0} checked out`
                );
            } else if (provider === 'innkey') {
                const res = await pmsAPI.syncInnkey(branchId);
                const d = res.data.data || {};
                setLastSync(new Date());
                toast.success(
                    `InnKey sync complete — ${d.checkedIn || 0} new, ${d.updated || 0} updated, ${d.shifted || 0} room shift(s), ${d.checkedOut || 0} checked out`
                );
            } else if (provider === 'hotelkey') {
                // HotelKey re-sends today's in-house guests as events; some
                // arrive with this read, the rest with the next minute's.
                const res = await pmsAPI.syncHotelkey(branchId);
                const d = res.data.data || {};
                setLastSync(new Date());
                toast.success(
                    `HotelKey: ${d.total || 0} event(s) read — ${d.checkedIn || 0} check-in(s), ${d.shifted || 0} room move(s), ${d.checkedOut || 0} checkout(s). In-house guests keep arriving over the next minute.`
                );
            } else {
                // CapitaLand / default
                const res = await roomAPI.syncPMS(branchId);
                const { updated, total, syncTime } = res.data.data;
                setLastSync(new Date(syncTime));
                toast.success(`PMS sync complete — ${updated} of ${total} room(s) updated`);
            }
            fetchAll();
        } catch (err) {
            toast.error(err.response?.data?.message || 'PMS sync failed');
        } finally {
            setSyncing(false);
        }
    };

    // ── CRUD ──────────────────────────────────────────────────────────────────
    const handleSaveRoom = async (e) => {
        e.preventDefault();
        if (!branchId) return toast.error('Select a branch first');
        setSaving(true);
        try {
            const payload = {
                ...roomForm, branchId, tenantId: admin?.tenantId,
                capacity: { maxPax: roomForm.capacity?.maxPax === '' || roomForm.capacity?.maxPax == null ? null : Number(roomForm.capacity.maxPax) },
            };
            if (editingRoom) {
                await roomAPI.update(editingRoom._id, payload);
                toast.success('Room updated');
            } else {
                await roomAPI.create(payload);
                toast.success('Room created');
            }
            setShowRoomModal(false);
            fetchAll();
        } catch (e) {
            toast.error(e.response?.data?.message || 'Save failed');
        } finally {
            setSaving(false);
        }
    };

    const handleCheckIn = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await roomAPI.checkIn(selectedRoom._id, guestForm);
            toast.success(`Guest checked in to Room ${selectedRoom.roomNumber}`);
            setShowCheckIn(false);
            setGuestForm(emptyGuest);
            fetchAll();
        } catch (e) {
            toast.error(e.response?.data?.message || 'Check-in failed');
        } finally {
            setSaving(false);
        }
    };

    const handleCheckOut = async () => {
        setSaving(true);
        try {
            await roomAPI.checkOut(selectedRoom._id, {});
            toast.success(`Room ${selectedRoom.roomNumber} checked out`);
            setShowCheckOut(false);
            fetchAll();
        } catch (e) {
            toast.error(e.response?.data?.message || 'Check-out failed');
        } finally {
            setSaving(false);
        }
    };

    const handleUpdateGuest = async (e) => {
        e.preventDefault();
        if (!editGuestForm.name.trim()) return toast.error('Guest name is required');
        setSaving(true);
        try {
            await roomAPI.updateGuest(selectedRoom._id, editGuestForm);
            toast.success(`Guest details updated for Room ${selectedRoom.roomNumber}`);
            setShowEditGuest(false);
            fetchAll();
        } catch (e) {
            toast.error(e.response?.data?.message || 'Update failed');
        } finally {
            setSaving(false);
        }
    };

    const handleExtendStay = async (e) => {
        e.preventDefault();
        if (!extendForm.newCheckOutDate) return toast.error('New checkout date is required');
        setSaving(true);
        try {
            await roomAPI.extendStay(selectedRoom._id, extendForm);
            toast.success(`Stay extended for Room ${selectedRoom.roomNumber}`);
            setShowExtend(false);
            setExtendForm(emptyExtend);
            fetchAll();
        } catch (e) {
            toast.error(e.response?.data?.message || 'Extend stay failed');
        } finally {
            setSaving(false);
        }
    };

    const openAuditLog = async (room) => {
        setSelectedRoom(room);
        setShowAudit(true);
        setLoadingAuditLog(true);
        setAuditLogs([]);
        setPmsEvents([]);
        try {
            const res = await roomAPI.getAuditLog(room._id);
            setAuditLogs(res.data.data?.logs || []);
            setPmsEvents(res.data.data?.pmsEvents || []);
        } catch (e) {
            toast.error(e.response?.data?.message || 'Failed to load audit log');
        } finally {
            setLoadingAuditLog(false);
        }
    };

    const handleGuestSearch = async (e) => {
        e.preventDefault();
        if (!guestSearchQuery.trim()) return;
        setSearchingGuests(true);
        try {
            const res = await roomAPI.searchGuests({ q: guestSearchQuery.trim(), branchId });
            setGuestSearchResults(res.data.data?.rooms || []);
        } catch (e) {
            toast.error(e.response?.data?.message || 'Search failed');
        } finally {
            setSearchingGuests(false);
        }
    };

    const handleDelete = async () => {
        setSaving(true);
        try {
            await roomAPI.delete(selectedRoom._id);
            toast.success('Room deleted');
            setShowDelete(false);
            fetchAll();
        } catch (e) {
            toast.error(e.response?.data?.message || 'Delete failed');
        } finally {
            setSaving(false);
        }
    };

    // ── CSV import: format → file checked row by row → import ──
    const openImport = () => {
        if (!branchId) return toast.warning('Select a branch first');
        setImportFile(null);
        setImportResult(null);
        setShowImportModal(true);
    };

    const downloadSampleCsv = () => {
        const blob = new Blob([ROOM_CSV_SAMPLE], { type: 'text/csv;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'rooms-import-sample.csv';
        a.click();
        URL.revokeObjectURL(url);
    };

    const handleCsvChosen = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;
        try {
            const rows = parseRoomsCsv(await file.text());
            setImportResult(null);
            setImportFile({ name: file.name, rows });
        } catch (err) {
            toast.error(err.message || 'Could not read the CSV file');
        }
    };

    const handleImportConfirm = async () => {
        const rooms = (importFile?.rows || []).filter(r => r.errors.length === 0).map(r => r.data);
        if (rooms.length === 0) return toast.warning('No valid rows to import');
        setImporting(true);
        try {
            const res = await roomAPI.bulkImport({ branchId, rooms });
            const result = res.data.data || {};
            setImportResult(result);
            toast.success(`Imported ${result.created || 0} room(s)${result.errors ? `, ${result.errors} skipped` : ''}`);
            fetchAll();
        } catch (err) {
            toast.error(err.response?.data?.message || 'CSV import failed');
        } finally {
            setImporting(false);
        }
    };

    const openEdit     = (room) => { setEditingRoom(room); setRoomForm({ roomNumber: room.roomNumber||'', roomName: room.roomName||'', roomType: room.roomType||'standard', floor: room.floor??'', building: room.building||'', status: room.status||'available', baseRate: room.baseRate??'', notes: room.notes||'', amenities: room.amenities||[], limits: room.limits||{maxDevices:3}, capacity: { maxPax: room.capacity?.maxPax ?? '' } }); setShowRoomModal(true); };
    const openAdd      = () => { setEditingRoom(null); setRoomForm(emptyRoom); setShowRoomModal(true); };
    const openCheckIn  = (room) => { setSelectedRoom(room); setGuestForm({ ...emptyGuest, checkInDate: toInputDate() }); setShowCheckIn(true); };
    const openEditGuest = (room) => {
        setSelectedRoom(room);
        const g = room.currentGuest || {};
        setEditGuestForm({
            name: g.name || '',
            email: g.email || '',
            mobile: g.mobile || '',
            checkInDate: g.checkInDate ? toInputDate(g.checkInDate) : '',
            checkOutDate: g.checkOutDate ? toInputDate(g.checkOutDate) : '',
            bookingId: g.bookingId || '',
            registrationNumber: g.registrationNumber || '',
            additionalGuests: g.additionalGuests ? [...g.additionalGuests] : [],
            adults: g.adults || 1,
            children: g.children || 0,
        });
        setShowEditGuest(true);
    };
    const openCheckOut = (room) => { setSelectedRoom(room); setShowCheckOut(true); };
    const openDelete   = (room) => { setSelectedRoom(room); setShowDelete(true); };
    const openExtend   = (room) => {
        setSelectedRoom(room);
        const current = room.currentGuest?.checkOutDate ? toInputDate(room.currentGuest.checkOutDate) : '';
        setExtendForm({ newCheckOutDate: current, reason: '' });
        setShowExtend(true);
    };
    const toggleAmenity = (a) => setRoomForm(p => ({ ...p, amenities: p.amenities.includes(a) ? p.amenities.filter(x => x !== a) : [...p.amenities, a] }));

    const openGuestDetails = (room) => setGuestRoomId(room._id);
    // The popup's buttons open the usual modal in its place.
    const fromGuestDetails = (open) => (room) => { setGuestRoomId(null); open(room); };
    const guestRoom = guestRoomId ? rooms.find(r => r._id === guestRoomId) : null;
    const guestRoomOccupied = guestRoom?.status === 'occupied' && !!guestRoom.currentGuest?.name;
    // Guest checked out (or the branch changed) while the popup was open: close
    // it, so it can't pop up again for the next guest of that room.
    useEffect(() => {
        if (guestRoomId && !guestRoomOccupied) setGuestRoomId(null);
    }, [guestRoomId, guestRoomOccupied]);

    const filtered = rooms.filter(r =>
        r.roomNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        r.currentGuest?.name?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // "Cleaning"/"Maintenance" stat cards removed — this property doesn't run a
    // housekeeping workflow, so checkout now goes straight to "available" and
    // those tiles always sat at 0. The statuses themselves still exist (Edit
    // Room can still set a room to cleaning/maintenance manually if ever needed).
    const statCards = [
        { key: 'total',       label: 'Total Rooms',  icon: 'fa-hotel',        color: 'purple', value: stats?.total       || rooms.length },
        { key: 'occupied',    label: 'Occupied',     icon: 'fa-user',         color: 'red',    value: stats?.occupied    || rooms.filter(r => r.status === 'occupied').length },
        { key: 'available',   label: 'Available',    icon: 'fa-check-circle', color: 'green',  value: stats?.available   || rooms.filter(r => r.status === 'available').length },
        // Informational only — statusSource isn't a Room.status value, so these
        // two can't drive the status filter chips the way the others do.
        { key: 'manual',      label: 'Manual Guests', icon: 'fa-user-edit',   color: 'orange', value: stats?.manual ?? rooms.filter(r => r.status === 'occupied' && r.statusSource === 'manual').length, infoOnly: true },
        { key: 'pms',         label: 'PMS Guests',    icon: 'fa-plug',        color: 'blue',   value: stats?.pms    ?? rooms.filter(r => r.status === 'occupied' && r.statusSource === 'pms_auto').length, infoOnly: true },
    ];

    if (!branchId) return (
        <div className="rooms-no-branch">
            <i className="fas fa-code-branch"></i>
            <h3>Select a Branch</h3>
            <p>Choose a branch from the top bar to manage its rooms.</p>
        </div>
    );

    return (
        <div className="rooms-page">

            {/* ── PMS Mode Banner ── */}
            {pmsEnabled && (() => {
                const provider     = selectedBranch?.integrations?.pms?.provider;
                const providerCfg  = selectedBranch?.integrations?.[provider] || {};
                const syncInterval = providerCfg.syncInterval || 5;
                const syncStatus   = providerCfg.lastSyncStatus;
                const isAutoSync   = ['wincloud','winhms','idsnext','hotla','innkey','hotelkey'].includes(provider);
                const isOffline    = isAutoSync && syncStatus === 'failed';

                return (
                    <div className={`pms-mode-banner ${isOffline ? 'pms-mode-banner-offline' : ''}`}>
                        <div className="pms-banner-left">
                            <div className="pms-banner-icon">
                                <i className={`fas ${isOffline ? 'fa-plug-circle-xmark' : 'fa-plug'}`}></i>
                            </div>
                            <div>
                                <strong>PMS Mode Active — {pmsProviderName}</strong>
                                {syncStatus && (
                                    <span className={`pms-status-badge pms-status-${syncStatus}`}>
                                        {syncStatus === 'success' ? '✓ Synced' : syncStatus === 'failed' ? '✗ Failed' : '○ Never'}
                                    </span>
                                )}
                                <p>
                                    {isOffline
                                        ? `${pmsProviderName} sync is failing — PMS unavailable. Manual room management is fully available below (Add Room / Check In / Check Out) while sync recovers.`
                                        : provider === 'hotla'
                                            ? `Rooms sync from ${pmsProviderName} every ${syncInterval} min, and each guest is also verified live again the moment they log in at the captive portal.`
                                            : provider === 'hotelkey'
                                            ? `Check-ins, room moves and check-outs arrive from ${pmsProviderName} as events, read every ${providerCfg.syncInterval || 1} min.`
                                            : isAutoSync
                                                ? `Rooms and guests sync automatically from ${pmsProviderName} every ${syncInterval} min.`
                                                : `Room occupancy updates automatically when guests connect via the captive portal.`}
                                    {lastSync && (
                                        <span className="pms-last-sync"> Last sync: {formatTime(lastSync)}</span>
                                    )}
                                </p>
                            </div>
                        </div>
                        <button
                            className={`pms-sync-btn ${syncing ? 'syncing' : ''}`}
                            onClick={handlePmsSync}
                            disabled={syncing}
                        >
                            <i className={`fas ${syncing ? 'fa-spinner fa-spin' : 'fa-sync-alt'}`}></i>
                            {syncing ? 'Syncing...' : 'Sync Now'}
                        </button>
                    </div>
                );
            })()}

            {/* ── Header ── */}
            <div className="rooms-header">
                <div className="rooms-header-text">
                    <h1><i className="fas fa-hotel"></i> Room Management</h1>
                    <p>
                        {pmsEnabled
                            ? <>PMS-managed rooms for <strong>{selectedBranch?.name}</strong> — auto-synced from {pmsProviderName}</>
                            : <>Manage rooms, guest check-in/check-out for <strong>{selectedBranch?.name}</strong></>
                        }
                    </p>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button className="btn-secondary-modern" onClick={() => setShowSearch(true)}>
                        <i className="fas fa-search"></i> Guest Search
                    </button>
                    <button className="btn-secondary-modern" onClick={openImport}>
                        <i className="fas fa-file-import"></i> Import CSV
                    </button>
                    <button className="btn-primary-modern" onClick={openAdd}>
                        <i className="fas fa-plus-circle"></i> Add Room
                    </button>
                </div>
            </div>

            {/* ── Stats ── */}
            <div className="rooms-stats-grid">
                {statCards.map(s => (
                    <div
                        key={s.key}
                        className="rooms-stat-card"
                        onClick={s.infoOnly ? undefined : () => setFilter(s.key === 'total' ? 'all' : s.key)}
                        style={{ cursor: s.infoOnly ? 'default' : 'pointer' }}
                    >
                        <div className={`rooms-stat-icon ${s.color}`}><i className={`fas ${s.icon}`}></i></div>
                        <div className="rooms-stat-info">
                            <div className="rooms-stat-value">{s.value}</div>
                            <div className="rooms-stat-label">{s.label}</div>
                        </div>
                    </div>
                ))}
            </div>

            {/* ── Toolbar ── */}
            <div className="rooms-toolbar">
                <div className="search-box-modern">
                    <i className="fas fa-search"></i>
                    <input placeholder="Search room or guest..." value={searchTerm} onChange={e => setSearch(e.target.value)} />
                </div>
                <div className="rooms-filters">
                    {['all', ...Object.keys(STATUS_CONFIG)].map(s => (
                        <button key={s} className={`filter-chip ${filterStatus === s ? 'active' : ''}`} onClick={() => setFilter(s)}>
                            {s === 'all' ? 'All' : STATUS_CONFIG[s].label}
                        </button>
                    ))}
                </div>
                <div className="view-toggle">
                    <button className={viewMode === 'grid' ? 'active' : ''} onClick={() => setViewMode('grid')}><i className="fas fa-th"></i></button>
                    <button className={viewMode === 'list' ? 'active' : ''} onClick={() => setViewMode('list')}><i className="fas fa-list"></i></button>
                </div>
            </div>

            {/* ── Room Grid / List ── */}
            {loading ? (
                <div className="loading-modern"><div className="spinner-modern"></div><p>Loading rooms...</p></div>
            ) : filtered.length === 0 ? (
                <div className="empty-state-modern">
                    <div className="empty-icon"><i className="fas fa-hotel"></i></div>
                    <h3>{searchTerm ? 'No rooms match your search' : 'No rooms yet'}</h3>
                    <p>{searchTerm ? 'Try a different search' : 'Click "Add Room" to create your first room.'}</p>
                </div>
            ) : viewMode === 'grid' ? (
                <div className="rooms-grid">
                    {filtered.map(room => (
                        <RoomCard
                            key={room._id}
                            room={room}
                            pmsEnabled={pmsEnabled}
                            onEdit={openEdit}
                            onCheckIn={openCheckIn}
                            onEditGuest={openEditGuest}
                            onCheckOut={openCheckOut}
                            onDelete={openDelete}
                            onExtend={openExtend}
                            onAudit={openAuditLog}
                            onShowGuest={openGuestDetails}
                        />
                    ))}
                </div>
            ) : (
                <div className={`rooms-list ${pmsEnabled ? '' : 'no-source'}`}>
                    <RoomListHeader pmsEnabled={pmsEnabled} />
                    {filtered.map(room => (
                        <RoomListRow
                            key={room._id}
                            room={room}
                            pmsEnabled={pmsEnabled}
                            onEdit={openEdit}
                            onCheckIn={openCheckIn}
                            onEditGuest={openEditGuest}
                            onCheckOut={openCheckOut}
                            onDelete={openDelete}
                            onExtend={openExtend}
                            onAudit={openAuditLog}
                            onShowGuest={openGuestDetails}
                        />
                    ))}
                </div>
            )}

            {/* ── Guest Details popup ── */}
            {guestRoomOccupied && (
                <GuestDetailsModal
                    room={guestRoom}
                    onClose={() => setGuestRoomId(null)}
                    onCheckOut={fromGuestDetails(openCheckOut)}
                    onEditGuest={fromGuestDetails(openEditGuest)}
                    onExtend={fromGuestDetails(openExtend)}
                    onAudit={fromGuestDetails(openAuditLog)}
                />
            )}

            {/* ── Add / Edit Room Modal ── */}
            {showRoomModal && (
                <Modal title={editingRoom ? `Edit Room ${editingRoom.roomNumber}` : 'Add New Room'} onClose={() => setShowRoomModal(false)}>
                    <form onSubmit={handleSaveRoom}>
                        <div className="modal-body-modern">
                            <div className="form-section">
                                <h3 className="section-title"><i className="fas fa-info-circle"></i> Room Details</h3>
                                <div className="form-grid">
                                    <div className="form-group-modern">
                                        <label>Room Number <span className="required">*</span></label>
                                        <input required value={roomForm.roomNumber} onChange={e => setRoomForm(p => ({ ...p, roomNumber: e.target.value }))} placeholder="e.g. 101" disabled={!!editingRoom} />
                                    </div>
                                    <div className="form-group-modern">
                                        <label>Room Name</label>
                                        <input value={roomForm.roomName} onChange={e => setRoomForm(p => ({ ...p, roomName: e.target.value }))} placeholder="e.g. Ocean Suite" />
                                    </div>
                                    <div className="form-group-modern">
                                        <label>Room Type</label>
                                        <select value={roomForm.roomType} onChange={e => setRoomForm(p => ({ ...p, roomType: e.target.value }))}>
                                            {ROOM_TYPES.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
                                        </select>
                                    </div>
                                    <div className="form-group-modern">
                                        <label>Status</label>
                                        <select value={roomForm.status} onChange={e => setRoomForm(p => ({ ...p, status: e.target.value }))}>
                                            {Object.entries(STATUS_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                                        </select>
                                    </div>
                                    <div className="form-group-modern">
                                        <label>Floor</label>
                                        <input type="number" value={roomForm.floor} onChange={e => setRoomForm(p => ({ ...p, floor: e.target.value }))} placeholder="Floor number" />
                                    </div>
                                    <div className="form-group-modern">
                                        <label>Building / Block</label>
                                        <input value={roomForm.building} onChange={e => setRoomForm(p => ({ ...p, building: e.target.value }))} placeholder="e.g. Wing A" />
                                    </div>
                                    <div className="form-group-modern">
                                        <label>Base Rate (₹/night)</label>
                                        <input type="number" value={roomForm.baseRate} onChange={e => setRoomForm(p => ({ ...p, baseRate: e.target.value }))} placeholder="0" />
                                    </div>
                                    <div className="form-group-modern">
                                        <label>Max Devices</label>
                                        <input type="number" min="1" value={roomForm.limits?.maxDevices} onChange={e => setRoomForm(p => ({ ...p, limits: { ...p.limits, maxDevices: Number(e.target.value) } }))} />
                                    </div>
                                    <div className="form-group-modern">
                                        <label>Room Capacity (max pax)</label>
                                        <input type="number" min="0" value={roomForm.capacity?.maxPax ?? ''} onChange={e => setRoomForm(p => ({ ...p, capacity: { maxPax: e.target.value } }))} placeholder="Unlimited" />
                                        <p className="input-hint">Leave blank for unlimited. Check-in is blocked if adults+children exceeds this.</p>
                                    </div>
                                </div>
                                <div className="form-group-modern full-width">
                                    <label>Notes</label>
                                    <textarea rows={2} value={roomForm.notes} onChange={e => setRoomForm(p => ({ ...p, notes: e.target.value }))} placeholder="Any special notes..." />
                                </div>
                            </div>
                            <div className="form-section">
                                <h3 className="section-title"><i className="fas fa-concierge-bell"></i> Amenities</h3>
                                <div className="amenities-grid">
                                    {Object.entries(AMENITY_ICONS).map(([a, icon]) => (
                                        <label key={a} className={`amenity-chip ${roomForm.amenities.includes(a) ? 'active' : ''}`}>
                                            <input type="checkbox" checked={roomForm.amenities.includes(a)} onChange={() => toggleAmenity(a)} />
                                            <i className={`fas ${icon}`}></i>
                                            {amenityLabel(a)}
                                        </label>
                                    ))}
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer-modern">
                            <button type="button" className="btn-secondary-modern" onClick={() => setShowRoomModal(false)}>Cancel</button>
                            <button type="submit" className="btn-primary-modern" disabled={saving}>
                                <i className="fas fa-save"></i> {saving ? 'Saving...' : editingRoom ? 'Update Room' : 'Create Room'}
                            </button>
                        </div>
                    </form>
                </Modal>
            )}

            {/* ── Check-In Modal ── */}
            {showCheckInModal && selectedRoom && (
                <Modal title={`Check In — Room ${selectedRoom.roomNumber}`} onClose={() => setShowCheckIn(false)}>
                    <form onSubmit={handleCheckIn}>
                        <div className="modal-body-modern">
                            {/* PMS mode info */}
                            {pmsEnabled && (
                                <div className="pms-checkin-note">
                                    <i className="fas fa-info-circle"></i>
                                    <div>
                                        <strong>PMS Mode</strong>
                                        <p>Rooms auto-update from {pmsProviderName}. Use this form for manual override only.</p>
                                    </div>
                                </div>
                            )}
                            <div className="form-section">
                                <h3 className="section-title"><i className="fas fa-user-plus"></i> Guest Information</h3>
                                <div className="form-grid">
                                    <div className="form-group-modern">
                                        <label>Guest Name <span className="required">*</span></label>
                                        <input required placeholder="Full name" value={guestForm.name} onChange={e => setGuestForm(p => ({ ...p, name: e.target.value }))} />
                                        <p className="input-hint">Primary guest — used for WiFi login (Room Number + Last Name).</p>
                                    </div>
                                    <div className="form-group-modern">
                                        <label>Adults</label>
                                        <input type="number" min="1" value={guestForm.adults} onChange={e => setGuestForm(p => ({ ...p, adults: Number(e.target.value) || 1 }))} />
                                    </div>
                                    <div className="form-group-modern">
                                        <label>Children</label>
                                        <input type="number" min="0" value={guestForm.children} onChange={e => setGuestForm(p => ({ ...p, children: Number(e.target.value) || 0 }))} />
                                        {selectedRoom?.capacity?.maxPax != null && (
                                            <p className="input-hint" style={{ color: (guestForm.adults + guestForm.children) > selectedRoom.capacity.maxPax ? '#dc2626' : undefined }}>
                                                Room capacity: {selectedRoom.capacity.maxPax} pax ({guestForm.adults + guestForm.children} selected)
                                            </p>
                                        )}
                                    </div>
                                    <div className="form-group-modern" style={{ gridColumn: '1 / -1' }}>
                                        <label>Additional Guests <span style={{ fontWeight: 400, color: '#6b7280' }}>(other names sharing this room — each can log in to WiFi with their own last name)</span></label>
                                        {guestForm.additionalGuests.map((g, i) => (
                                            <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
                                                <input
                                                    placeholder={`Guest ${i + 2} name`}
                                                    value={g}
                                                    onChange={e => setGuestForm(p => {
                                                        const next = [...p.additionalGuests];
                                                        next[i] = e.target.value;
                                                        return { ...p, additionalGuests: next };
                                                    })}
                                                />
                                                <button type="button" className="action-btn-icon delete"
                                                    onClick={() => setGuestForm(p => ({ ...p, additionalGuests: p.additionalGuests.filter((_, idx) => idx !== i) }))}>
                                                    <i className="fas fa-times"></i>
                                                </button>
                                            </div>
                                        ))}
                                        <button type="button" className="btn-secondary-modern"
                                            onClick={() => setGuestForm(p => ({ ...p, additionalGuests: [...p.additionalGuests, ''] }))}>
                                            <i className="fas fa-plus"></i> Add Guest
                                        </button>
                                    </div>
                                    <div className="form-group-modern">
                                        <label>Mobile</label>
                                        <input placeholder="+91 99999 99999" value={guestForm.mobile} onChange={e => setGuestForm(p => ({ ...p, mobile: e.target.value }))} />
                                    </div>
                                    <div className="form-group-modern">
                                        <label>Email</label>
                                        <input type="email" placeholder="guest@email.com" value={guestForm.email} onChange={e => setGuestForm(p => ({ ...p, email: e.target.value }))} />
                                    </div>
                                    <div className="form-group-modern">
                                        <label>Booking / Reservation ID</label>
                                        <input placeholder="PMS booking reference" value={guestForm.bookingId} onChange={e => setGuestForm(p => ({ ...p, bookingId: e.target.value }))} />
                                    </div>
                                    <div className="form-group-modern">
                                        <label>Registration Number</label>
                                        <input placeholder="Front-desk folio/reg number" value={guestForm.registrationNumber} onChange={e => setGuestForm(p => ({ ...p, registrationNumber: e.target.value }))} />
                                    </div>
                                    <div className="form-group-modern">
                                        <label>Check-In Date <span className="required">*</span></label>
                                        <DateInput required type="date" value={guestForm.checkInDate} onChange={e => setGuestForm(p => ({ ...p, checkInDate: e.target.value }))} />
                                    </div>
                                    <div className="form-group-modern">
                                        <label>Check-Out Date</label>
                                        <DateInput type="date" min={guestForm.checkInDate} value={guestForm.checkOutDate} onChange={e => setGuestForm(p => ({ ...p, checkOutDate: e.target.value }))} />
                                    </div>
                                </div>
                            </div>
                            {!pmsEnabled && (
                                <div className="checkin-info-note">
                                    <i className="fas fa-wifi"></i>
                                    <span>Wi-Fi access granted after check-in. Guest authenticates via <strong>Room Number + Last Name</strong> at the captive portal.</span>
                                </div>
                            )}
                        </div>
                        <div className="modal-footer-modern">
                            <button type="button" className="btn-secondary-modern" onClick={() => setShowCheckIn(false)}>Cancel</button>
                            <button type="submit" className="btn-primary-modern" disabled={saving}>
                                <i className="fas fa-sign-in-alt"></i> {saving ? 'Processing...' : pmsEnabled ? 'Manual Check In' : 'Check In Guest'}
                            </button>
                        </div>
                    </form>
                </Modal>
            )}

            {/* ── Edit Guest — name/pax/additional guests for an already-occupied room, PMS-synced or manual ── */}
            {showEditGuestModal && selectedRoom && (
                <Modal title={`Edit Guest — Room ${selectedRoom.roomNumber}`} onClose={() => setShowEditGuest(false)}>
                    <form onSubmit={handleUpdateGuest}>
                        <div className="modal-body-modern">
                            {getSourceBadge(selectedRoom) && (
                                <div className="pms-checkin-note">
                                    <i className="fas fa-info-circle"></i>
                                    <div>
                                        <strong>{getSourceBadge(selectedRoom).label}</strong>
                                        <p>This room's guest data came from {selectedRoom.sourceSystem || 'a PMS'}. Edits here are saved to our database and are never overwritten by a later sync — only newly-created rooms pick up fresh PMS data.</p>
                                    </div>
                                </div>
                            )}
                            <div className="form-section">
                                <h3 className="section-title"><i className="fas fa-user-edit"></i> Guest Information</h3>
                                <div className="form-grid">
                                    <div className="form-group-modern">
                                        <label>Guest Name <span className="required">*</span></label>
                                        <input required placeholder="Full name" value={editGuestForm.name} onChange={e => setEditGuestForm(p => ({ ...p, name: e.target.value }))} />
                                        <p className="input-hint">Primary guest — used for WiFi login (Room Number + Last Name).</p>
                                    </div>
                                    <div className="form-group-modern">
                                        <label>Adults</label>
                                        <input type="number" min="1" value={editGuestForm.adults} onChange={e => setEditGuestForm(p => ({ ...p, adults: Number(e.target.value) || 1 }))} />
                                    </div>
                                    <div className="form-group-modern">
                                        <label>Children</label>
                                        <input type="number" min="0" value={editGuestForm.children} onChange={e => setEditGuestForm(p => ({ ...p, children: Number(e.target.value) || 0 }))} />
                                        {selectedRoom?.capacity?.maxPax != null && (
                                            <p className="input-hint" style={{ color: (editGuestForm.adults + editGuestForm.children) > selectedRoom.capacity.maxPax ? '#dc2626' : undefined }}>
                                                Room capacity: {selectedRoom.capacity.maxPax} pax ({editGuestForm.adults + editGuestForm.children} selected)
                                            </p>
                                        )}
                                    </div>
                                    <div className="form-group-modern" style={{ gridColumn: '1 / -1' }}>
                                        <label>Additional Guests <span style={{ fontWeight: 400, color: '#6b7280' }}>(other names sharing this room — each can log in to WiFi with their own last name)</span></label>
                                        {editGuestForm.additionalGuests.map((g, i) => (
                                            <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
                                                <input
                                                    placeholder={`Guest ${i + 2} name`}
                                                    value={g}
                                                    onChange={e => setEditGuestForm(p => {
                                                        const next = [...p.additionalGuests];
                                                        next[i] = e.target.value;
                                                        return { ...p, additionalGuests: next };
                                                    })}
                                                />
                                                <button type="button" className="action-btn-icon delete"
                                                    onClick={() => setEditGuestForm(p => ({ ...p, additionalGuests: p.additionalGuests.filter((_, idx) => idx !== i) }))}>
                                                    <i className="fas fa-times"></i>
                                                </button>
                                            </div>
                                        ))}
                                        <button type="button" className="btn-secondary-modern"
                                            onClick={() => setEditGuestForm(p => ({ ...p, additionalGuests: [...p.additionalGuests, ''] }))}>
                                            <i className="fas fa-plus"></i> Add Guest
                                        </button>
                                    </div>
                                    <div className="form-group-modern">
                                        <label>Mobile</label>
                                        <input placeholder="+91 99999 99999" value={editGuestForm.mobile} onChange={e => setEditGuestForm(p => ({ ...p, mobile: e.target.value }))} />
                                    </div>
                                    <div className="form-group-modern">
                                        <label>Email</label>
                                        <input type="email" placeholder="guest@email.com" value={editGuestForm.email} onChange={e => setEditGuestForm(p => ({ ...p, email: e.target.value }))} />
                                    </div>
                                    <div className="form-group-modern">
                                        <label>Booking / Reservation ID</label>
                                        <input placeholder="PMS booking reference" value={editGuestForm.bookingId} onChange={e => setEditGuestForm(p => ({ ...p, bookingId: e.target.value }))} />
                                    </div>
                                    <div className="form-group-modern">
                                        <label>Registration Number</label>
                                        <input placeholder="Front-desk folio/reg number" value={editGuestForm.registrationNumber} onChange={e => setEditGuestForm(p => ({ ...p, registrationNumber: e.target.value }))} />
                                    </div>
                                    <div className="form-group-modern">
                                        <label>Check-In Date</label>
                                        <DateInput type="date" value={editGuestForm.checkInDate} disabled title="Check-in date is set at actual check-in and isn't editable here." />
                                    </div>
                                    <div className="form-group-modern">
                                        <label>Check-Out Date</label>
                                        <DateInput type="date" min={editGuestForm.checkInDate} value={editGuestForm.checkOutDate} onChange={e => setEditGuestForm(p => ({ ...p, checkOutDate: e.target.value }))} />
                                        <p className="input-hint">To change checkout with a tracked reason, use Extend Stay instead.</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer-modern">
                            <button type="button" className="btn-secondary-modern" onClick={() => setShowEditGuest(false)}>Cancel</button>
                            <button type="submit" className="btn-primary-modern" disabled={saving}>
                                <i className="fas fa-save"></i> {saving ? 'Saving...' : 'Save Changes'}
                            </button>
                        </div>
                    </form>
                </Modal>
            )}

            {/* ── Check-Out Confirmation ── */}
            {showCheckOutModal && selectedRoom && (
                <Modal title={`Check Out — Room ${selectedRoom.roomNumber}`} onClose={() => setShowCheckOut(false)} small>
                    <div className="modal-body-modern">
                        <div className="warning-box">
                            <i className="fas fa-sign-out-alt"></i>
                            <div>
                                <p>Check out <strong>{selectedRoom.currentGuest?.name}</strong> from Room <strong>{selectedRoom.roomNumber}</strong>?</p>
                                <p className="warning-text">Internet access will be revoked. Room moves back to <strong>Available</strong>.</p>
                                {pmsEnabled && selectedRoom.pms?.syncStatus === 'synced' && (
                                    <p className="warning-text" style={{ color: '#0369a1', marginTop: 4 }}>
                                        <i className="fas fa-plug" style={{ marginRight: 4 }}></i>
                                        This room was checked in via PMS. The next PMS sync will not re-occupy it unless the guest logs in again.
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="modal-footer-modern">
                        <button className="btn-secondary-modern" onClick={() => setShowCheckOut(false)}>Cancel</button>
                        <button className="btn-primary-modern" onClick={handleCheckOut} disabled={saving}
                            style={{ background: 'linear-gradient(135deg, #f59e0b, #d97706)' }}>
                            <i className="fas fa-sign-out-alt"></i> {saving ? 'Processing...' : 'Confirm Check-Out'}
                        </button>
                    </div>
                </Modal>
            )}

            {/* ── Delete Confirmation ── */}
            {/* ── Import CSV: format, then the file's rows, then the import ── */}
            {showImportModal && (() => {
                const rows = importFile?.rows || [];
                const valid = rows.filter(r => r.errors.length === 0);
                const invalid = rows.filter(r => r.errors.length > 0);
                const step = importResult ? 3 : (importFile ? 2 : 1);
                return (
                    <Modal title={`Import Rooms from CSV — ${selectedBranch?.name || ''}`} onClose={() => setShowImportModal(false)}>
                        <div className="modal-body-modern">
                            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', fontSize: '0.8rem' }}>
                                {['1. Format', '2. Check rows', '3. Import'].map((label, i) => (
                                    <span key={label} style={{
                                        padding: '0.25rem 0.7rem', borderRadius: '999px', fontWeight: 600,
                                        background: step === i + 1 ? '#6d28d9' : (step > i + 1 ? '#ddd6fe' : '#f3f4f6'),
                                        color: step === i + 1 ? '#fff' : (step > i + 1 ? '#5b21b6' : '#6b7280'),
                                    }}>{label}</span>
                                ))}
                            </div>

                            {step === 1 && (<>
                                <p className="input-hint" style={{ marginBottom: '0.75rem' }}>
                                    One row per room. The first row must be the column names below (any order; only <strong>roomNumber</strong> is required).
                                    A room number already in this branch is skipped, never changed.
                                </p>
                                <table className="data-table-modern" style={{ fontSize: '0.8rem', marginBottom: '0.75rem' }}>
                                    <thead><tr><th>Column</th><th>Required</th><th>Values</th><th>Example</th></tr></thead>
                                    <tbody>
                                        {ROOM_CSV_COLUMNS.map(c => (
                                            <tr key={c.key}>
                                                <td><code>{c.key}</code></td>
                                                <td>{c.required ? 'Yes' : 'No'}</td>
                                                <td>{c.values}</td>
                                                <td>{c.example}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                <pre style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '6px', padding: '0.6rem', fontSize: '0.75rem', overflowX: 'auto' }}>{ROOM_CSV_SAMPLE}</pre>
                            </>)}

                            {step === 2 && (<>
                                <p className="input-hint" style={{ marginBottom: '0.75rem' }}>
                                    <strong>{importFile.name}</strong>: {rows.length} row(s) — <span style={{ color: '#059669' }}>{valid.length} ready</span>
                                    {invalid.length > 0 && <>, <span style={{ color: '#dc2626' }}>{invalid.length} with problems</span> (those rows are left out)</>}
                                </p>
                                <div style={{ maxHeight: '40vh', overflow: 'auto' }}>
                                    <table className="data-table-modern" style={{ fontSize: '0.78rem' }}>
                                        <thead><tr><th>#</th><th>Room</th><th>Name</th><th>Type</th><th>Floor</th><th>Building</th><th>Rate</th><th>Check</th></tr></thead>
                                        <tbody>
                                            {rows.map((r, i) => (
                                                <tr key={i} style={r.errors.length ? { background: '#fef2f2' } : undefined}>
                                                    <td>{i + 2}</td>
                                                    <td>{r.data.roomNumber || <em>—</em>}</td>
                                                    <td>{r.data.roomName || ''}</td>
                                                    <td>{r.data.roomType || 'standard'}</td>
                                                    <td>{r.data.floor ?? ''}</td>
                                                    <td>{r.data.building || ''}</td>
                                                    <td>{r.data.baseRate ?? ''}</td>
                                                    <td style={{ color: r.errors.length ? '#dc2626' : '#059669' }}>{r.errors.length ? r.errors.join('; ') : 'OK'}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </>)}

                            {step === 3 && (<>
                                <p style={{ marginBottom: '0.5rem' }}>
                                    <strong style={{ color: '#059669' }}>{importResult.created || 0} room(s) imported.</strong>
                                    {importResult.errors > 0 && <> <span style={{ color: '#b45309' }}>{importResult.errors} skipped:</span></>}
                                </p>
                                {importResult.errorDetails?.length > 0 && (
                                    <ul style={{ fontSize: '0.8rem', color: '#6b7280', maxHeight: '30vh', overflow: 'auto', paddingLeft: '1.2rem' }}>
                                        {importResult.errorDetails.map((d, i) => <li key={i}>Room {d.roomNumber}: {d.error}</li>)}
                                    </ul>
                                )}
                            </>)}
                        </div>
                        <div className="modal-footer-modern">
                            <input type="file" accept=".csv,text/csv" ref={csvInputRef} onChange={handleCsvChosen} style={{ display: 'none' }} />
                            {step === 1 && (<>
                                <button className="btn-secondary-modern" onClick={downloadSampleCsv}><i className="fas fa-download"></i> Download sample CSV</button>
                                <button className="btn-primary-modern" onClick={() => csvInputRef.current?.click()}><i className="fas fa-folder-open"></i> Choose CSV file</button>
                            </>)}
                            {step === 2 && (<>
                                <button className="btn-secondary-modern" onClick={() => setImportFile(null)}>Back</button>
                                <button className="btn-secondary-modern" onClick={() => csvInputRef.current?.click()}>Choose another file</button>
                                <button className="btn-primary-modern" onClick={handleImportConfirm} disabled={importing || valid.length === 0}>
                                    <i className="fas fa-file-import"></i> {importing ? 'Importing...' : `Import ${valid.length} room(s)`}
                                </button>
                            </>)}
                            {step === 3 && (
                                <button className="btn-primary-modern" onClick={() => setShowImportModal(false)}>Done</button>
                            )}
                        </div>
                    </Modal>
                );
            })()}

            {showDeleteModal && selectedRoom && (
                <Modal title="Delete Room" onClose={() => setShowDelete(false)} small danger>
                    <div className="modal-body-modern">
                        <div className="warning-box">
                            <i className="fas fa-exclamation-triangle"></i>
                            <div>
                                <p>Delete <strong>Room {selectedRoom.roomNumber}</strong>?</p>
                                <p className="warning-text">This action cannot be undone.</p>
                            </div>
                        </div>
                    </div>
                    <div className="modal-footer-modern">
                        <button className="btn-secondary-modern" onClick={() => setShowDelete(false)}>Cancel</button>
                        <button className="btn-danger-modern" onClick={handleDelete} disabled={saving}>
                            <i className="fas fa-trash"></i> {saving ? 'Deleting...' : 'Delete Room'}
                        </button>
                    </div>
                </Modal>
            )}

            {/* ── Extend Stay ── */}
            {showExtendModal && selectedRoom && (
                <Modal title={`Extend Stay — Room ${selectedRoom.roomNumber}`} onClose={() => setShowExtend(false)} small>
                    <form onSubmit={handleExtendStay}>
                        <div className="modal-body-modern">
                            {selectedRoom.currentGuest?.checkOutDate && (
                                <p className="input-hint" style={{ marginBottom: '0.75rem' }}>
                                    Current checkout: <strong>{formatDateTime(selectedRoom.currentGuest.checkOutDate)}</strong>
                                </p>
                            )}
                            <div className="form-grid">
                                <div className="form-group-modern full-width">
                                    <label>New Checkout Date <span className="required">*</span></label>
                                    <DateInput required type="date" value={extendForm.newCheckOutDate} onChange={e => setExtendForm(p => ({ ...p, newCheckOutDate: e.target.value }))} />
                                </div>
                                <div className="form-group-modern full-width">
                                    <label>Reason</label>
                                    <input placeholder="e.g. Late Checkout" value={extendForm.reason} onChange={e => setExtendForm(p => ({ ...p, reason: e.target.value }))} />
                                </div>
                            </div>
                            {selectedRoom.stayExtensions?.length > 0 && (
                                <p className="input-hint" style={{ marginTop: '0.5rem' }}>
                                    This stay has already been extended {selectedRoom.stayExtensions.length} time{selectedRoom.stayExtensions.length > 1 ? 's' : ''} — full history is in the room's audit log.
                                </p>
                            )}
                        </div>
                        <div className="modal-footer-modern">
                            <button type="button" className="btn-secondary-modern" onClick={() => setShowExtend(false)}>Cancel</button>
                            <button type="submit" className="btn-primary-modern" disabled={saving}>
                                <i className="fas fa-clock"></i> {saving ? 'Saving...' : 'Extend Stay'}
                            </button>
                        </div>
                    </form>
                </Modal>
            )}

            {/* ── Audit Log ── */}
            {showAuditModal && selectedRoom && (
                <Modal title={`Audit Log — Room ${selectedRoom.roomNumber}`} onClose={() => setShowAudit(false)}>
                    <div className="modal-body-modern">
                        {!loadingAuditLog && pmsEvents.length > 0 && (
                            <>
                                <h4 className="audit-log-section-title">PMS Room Events</h4>
                                <div className="audit-log-list">
                                    {pmsEvents.map(ev => (
                                        <div key={ev._id} className="audit-log-row audit-log-row--pms">
                                            <div className="audit-log-action">
                                                <i className={`fas ${PMS_EVENT_ICONS[ev.type] || 'fa-circle-info'}`}></i>
                                                {describePmsEvent(ev, selectedRoom.roomNumber)}
                                            </div>
                                            {ev.type === 'guest_change' && pmsChangeLines(ev.details).map(line => (
                                                <div key={line} className="audit-log-detail">{line}</div>
                                            ))}
                                            <div className="audit-log-meta">
                                                <span>{ev.guestName || '—'}</span>
                                                <span>{(ev.provider || 'PMS').toUpperCase()}</span>
                                                <span>{formatDateTime(ev.createdAt)}</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                {auditLogs.length > 0 && <h4 className="audit-log-section-title">Admin Changes</h4>}
                            </>
                        )}
                        {loadingAuditLog ? (
                            <div className="loading-modern"><div className="spinner-modern"></div><p>Loading...</p></div>
                        ) : auditLogs.length === 0 ? (
                            pmsEvents.length === 0 && <p className="input-hint">No audit history recorded for this room yet.</p>
                        ) : (
                            <div className="audit-log-list">
                                {auditLogs.map(log => (
                                    <div key={log._id} className="audit-log-row">
                                        <div className="audit-log-action">
                                            <i className={`fas ${AUDIT_ACTION_ICONS[log.action] || 'fa-circle-info'}`}></i>
                                            {AUDIT_ACTION_LABELS[log.action] || log.action}
                                        </div>
                                        <div className="audit-log-meta">
                                            <span>{log.actorId?.name || log.actorId?.email || (log.source === 'pms_auto' ? 'PMS sync' : 'System')}</span>
                                            <span>{formatDateTime(log.createdAt)}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                    <div className="modal-footer-modern">
                        <button className="btn-secondary-modern" onClick={() => setShowAudit(false)}>Close</button>
                    </div>
                </Modal>
            )}

            {/* ── Global Guest Search ── */}
            {showSearchModal && (
                <Modal title="Guest Search" onClose={() => { setShowSearch(false); setGuestSearchResults([]); setGuestSearchQuery(''); }}>
                    <div className="modal-body-modern">
                        <form onSubmit={handleGuestSearch} style={{ display: 'flex', gap: 8, marginBottom: '1rem' }}>
                            <input
                                autoFocus
                                placeholder="Room, name, mobile, email, booking or registration number..."
                                value={guestSearchQuery}
                                onChange={e => setGuestSearchQuery(e.target.value)}
                                style={{ flex: 1 }}
                            />
                            <button type="submit" className="btn-primary-modern" disabled={searchingGuests}>
                                <i className={`fas ${searchingGuests ? 'fa-spinner fa-spin' : 'fa-search'}`}></i>
                            </button>
                        </form>
                        {guestSearchResults.length > 0 ? (
                            <div className="audit-log-list">
                                {guestSearchResults.map(r => {
                                    const badge = getSourceBadge(r);
                                    return (
                                        <div key={r._id} className="audit-log-row">
                                            <div className="audit-log-action">
                                                <strong>Room {r.roomNumber}</strong> — {r.currentGuest?.name || <em>vacant</em>}
                                                {badge && <span className={`src-badge ${badge.className}`} title={badge.title} style={{ marginLeft: 8 }}>{badge.label}</span>}
                                            </div>
                                            <div className="audit-log-meta">
                                                <span>{r.branchId?.name}</span>
                                                <span>{r.status === 'occupied' ? 'Active' : 'Checked Out'}</span>
                                                <span>{formatDate(r.currentGuest?.checkOutDate)}</span>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            !searchingGuests && guestSearchQuery && <p className="input-hint">No matches found.</p>
                        )}
                    </div>
                </Modal>
            )}
        </div>
    );
}

/* ── Room "More Actions" Menu ──────────────────────────────────────────────────
 * Both .room-card and .rooms-list use overflow:hidden for their rounded
 * corners, which would clip a normal absolutely-positioned dropdown — so
 * this renders into document.body via a portal, positioned from the
 * trigger button's actual screen coordinates (position:fixed), which
 * escapes that clipping entirely regardless of which view it's opened from.
 */
function RoomMoreMenu({ room, onEditGuest, onExtend, onAudit, onEdit, onDelete }) {
    const [open, setOpen] = useState(false);
    const [pos, setPos] = useState(null);
    const btnRef  = useRef(null);
    const menuRef = useRef(null);

    const toggle = () => {
        if (!open && btnRef.current) {
            const rect = btnRef.current.getBoundingClientRect();
            setPos({
                bottom: window.innerHeight - rect.top + 4, // open upward, small gap above the button
                right:  window.innerWidth - rect.right,     // right-align with the button
            });
        }
        setOpen(v => !v);
    };

    useEffect(() => {
        if (!open) return;
        const closeOnOutsideClick = (e) => {
            if (menuRef.current?.contains(e.target) || btnRef.current?.contains(e.target)) return;
            setOpen(false);
        };
        // Scroll/resize can invalidate the computed position — simplest safe
        // behavior is to just close, rather than track and re-measure live.
        const closeOnScrollOrResize = () => setOpen(false);
        document.addEventListener('mousedown', closeOnOutsideClick);
        window.addEventListener('scroll', closeOnScrollOrResize, true);
        window.addEventListener('resize', closeOnScrollOrResize);
        return () => {
            document.removeEventListener('mousedown', closeOnOutsideClick);
            window.removeEventListener('scroll', closeOnScrollOrResize, true);
            window.removeEventListener('resize', closeOnScrollOrResize);
        };
    }, [open]);

    const isOccupied = room.status === 'occupied';
    const items = [
        isOccupied && { icon: 'fa-user-edit', label: 'Edit Guest',    onClick: () => onEditGuest(room) },
        isOccupied && { icon: 'fa-clock',     label: 'Extend Stay',   onClick: () => onExtend(room) },
        { icon: 'fa-history', label: 'Audit History', onClick: () => onAudit(room) },
        { icon: 'fa-edit',    label: 'Edit Room',      onClick: () => onEdit(room) },
        { icon: 'fa-trash',   label: 'Delete Room',    onClick: () => onDelete(room), danger: true },
    ].filter(Boolean);

    return (
        <>
            <button ref={btnRef} type="button" className="action-btn-icon" onClick={toggle} title="More actions">
                <i className="fas fa-ellipsis-v"></i>
            </button>
            {open && pos && createPortal(
                <div ref={menuRef} className="room-menu-dropdown" style={{ bottom: pos.bottom, right: pos.right }}>
                    {items.map(item => (
                        <button
                            key={item.label}
                            type="button"
                            className={item.danger ? 'danger' : ''}
                            onClick={() => { setOpen(false); item.onClick(); }}
                        >
                            <i className={`fas ${item.icon}`}></i> {item.label}
                        </button>
                    ))}
                </div>,
                document.body
            )}
        </>
    );
}

/* ── Room Card ─────────────────────────────────────────────────────────────── */
function RoomCard({ room, pmsEnabled, onEdit, onCheckIn, onEditGuest, onCheckOut, onDelete, onExtend, onAudit, onShowGuest }) {
    const s = STATUS_CONFIG[room.status] || STATUS_CONFIG.available;
    const guest = room.currentGuest;
    const checkOut = guest?.checkOutDate ? new Date(guest.checkOutDate) : null;
    const daysLeft = checkOut ? Math.ceil((checkOut - Date.now()) / 86400000) : null;
    const sourceBadge = getSourceBadge(room);
    const showGuest = () => onShowGuest(room);

    return (
        <div className="room-card">
            <div className={`room-card-header status-${room.status}`} style={{ borderLeftColor: s.color }}>
                <div className="room-number-badge" style={{ color: s.color }} title={room.roomName || undefined}>
                    <i className={`fas ${s.icon}`}></i>
                    <span>{room.roomNumber}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    {sourceBadge && (
                        <span className={`src-badge ${sourceBadge.className}`} title={sourceBadge.title}>
                            <i className="fas fa-plug"></i> {sourceBadge.label}
                        </span>
                    )}
                    <span className="room-status-pill solid" style={{ background: s.color }}>
                        {s.label}
                    </span>
                </div>
            </div>

            <div className="room-card-body">
                {(room.floor || room.building) && (
                    <div className="room-meta">
                        {room.floor    && <span className="room-floor"><i className="fas fa-layer-group"></i> Floor {room.floor}</span>}
                        {room.building && <span className="room-floor"><i className="fas fa-building"></i> {room.building}</span>}
                    </div>
                )}

                {room.status === 'occupied' && guest?.name ? (
                    <div
                        className="guest-info-card clickable"
                        role="button"
                        tabIndex={0}
                        title="Guest details"
                        onClick={showGuest}
                        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); showGuest(); } }}
                    >
                        <div className="guest-avatar">{guest.name.charAt(0).toUpperCase()}</div>
                        <div className="guest-details">
                            <div className="guest-name">
                                {guest.name}
                                {guest.additionalGuests?.length > 0 && (
                                    <span className="days-badge" title={guest.additionalGuests.join(', ')} style={{ marginLeft: 6 }}>
                                        +{guest.additionalGuests.length} guest{guest.additionalGuests.length > 1 ? 's' : ''}
                                    </span>
                                )}
                            </div>
                            {(guest.adults > 1 || guest.children > 0) && (
                                <div className="guest-contact"><i className="fas fa-users"></i> {guest.adults || 1} adult{(guest.adults || 1) > 1 ? 's' : ''}{guest.children > 0 ? `, ${guest.children} child${guest.children > 1 ? 'ren' : ''}` : ''}</div>
                            )}
                            {guest.mobile && <div className="guest-contact"><i className="fas fa-phone"></i> {guest.mobile}</div>}
                            {guest.checkInDate && (
                                <div className="guest-stay-line" title="Checked in">
                                    <i className="fas fa-sign-in-alt"></i>
                                    <span className="guest-stay-label">In</span>
                                    {formatCheckIn(guest.checkInDate)}
                                </div>
                            )}
                            {checkOut && (
                                <div className={`guest-checkout ${daysLeft <= 1 ? 'urgent' : ''}`} title="Check-out">
                                    <i className="fas fa-calendar-check"></i>
                                    <span className="guest-stay-label">Out</span>
                                    {formatDate(checkOut)}
                                    {daysLeft !== null && <span className="days-badge">{daysLeft}d left</span>}
                                </div>
                            )}
                        </div>
                        <i className="fas fa-chevron-right guest-info-chevron" aria-hidden="true"></i>
                    </div>
                ) : (
                    <div className="room-vacant-msg">
                        <i className={`fas ${s.icon}`} style={{ color: s.color }}></i>
                        <span>{s.label}</span>
                    </div>
                )}

                {room.amenities?.length > 0 && (
                    <div className="amenities-row">
                        {room.amenities.slice(0, 5).map(a => (
                            <span key={a} className="amenity-dot" title={amenityLabel(a)}><i className={`fas ${AMENITY_ICONS[a] || 'fa-star'}`}></i></span>
                        ))}
                        {room.amenities.length > 5 && <span className="amenity-dot">+{room.amenities.length - 5}</span>}
                    </div>
                )}
            </div>

            <div className="room-card-footer">
                {room.status === 'available' && (
                    <button className="room-action-btn checkin" onClick={() => onCheckIn(room)}>
                        <i className="fas fa-sign-in-alt"></i> {pmsEnabled ? 'Manual Check In' : 'Check In'}
                    </button>
                )}
                {room.status === 'occupied' && (
                    <button className="room-action-btn checkout" onClick={() => onCheckOut(room)}>
                        <i className="fas fa-sign-out-alt"></i> Check Out
                    </button>
                )}
                {(room.status === 'cleaning' || room.status === 'maintenance') && (
                    <button className="room-action-btn available" onClick={() => onEdit({ ...room, _editStatus: true })}>
                        <i className="fas fa-check"></i> Mark Available
                    </button>
                )}
                <div className="room-icon-actions">
                    <RoomMoreMenu room={room} onEditGuest={onEditGuest} onExtend={onExtend} onAudit={onAudit} onEdit={onEdit} onDelete={onDelete} />
                </div>
            </div>
        </div>
    );
}

/* ── Guest Details popup ───────────────────────────────────────────────────── */
function GuestDetailsModal({ room, onClose, onCheckOut, onEditGuest, onExtend, onAudit }) {
    const g = room.currentGuest || {};
    const s = STATUS_CONFIG[room.status] || STATUS_CONFIG.occupied;
    const sourceBadge = getSourceBadge(room);
    const checkOut = g.checkOutDate ? new Date(g.checkOutDate) : null;
    const daysLeft = checkOut ? Math.ceil((checkOut - Date.now()) / 86400000) : null;
    const adults = g.adults || 1;
    const children = g.children || 0;
    const fromPms = new Set((g.pmsAdditionalGuests || []).map(n => n.trim().toLowerCase()));
    const attributes = Object.entries(g.pmsAttributes || {}).filter(([, v]) => v !== '' && v !== null && v !== undefined);

    // Always shown: stay dates and how to reach the guest. The rest only when set.
    const fields = [
        { label: 'Check-in',  icon: 'fa-sign-in-alt', value: formatCheckIn(g.checkInDate), always: true },
        {
            label: 'Check-out', icon: 'fa-sign-out-alt', always: true,
            value: checkOut ? (
                <>
                    {formatDateTime(checkOut)}
                    {daysLeft !== null && <span className={`days-badge ${daysLeft <= 1 ? 'urgent' : ''}`}>{daysLeft}d left</span>}
                </>
            ) : '—',
        },
        { label: 'Mobile', icon: 'fa-phone',    value: g.mobile, always: true },
        { label: 'Email',  icon: 'fa-envelope', value: g.email },
        { label: 'Occupancy', icon: 'fa-users', value: `${adults} adult${adults > 1 ? 's' : ''}${children ? `, ${children} child${children > 1 ? 'ren' : ''}` : ''}` },
        { label: 'Reservation / Booking ID', icon: 'fa-hashtag',     value: g.bookingId },
        { label: 'Registration No.',         icon: 'fa-id-card',     value: g.registrationNumber },
        { label: 'PMS Guest ID',             icon: 'fa-fingerprint', value: g.guestId },
        { label: 'ID Document',              icon: 'fa-passport',    value: g.idDocumentType ? (ID_DOCUMENT_LABELS[g.idDocumentType] || g.idDocumentType) : '' },
        { label: 'Internet Plan',            icon: 'fa-wifi',        value: room.assignedPlanId?.name },
        { label: 'Last PMS sync',            icon: 'fa-sync-alt',    value: room.pms?.lastSyncAt ? formatDateTime(room.pms.lastSyncAt) : '' },
    ].filter(f => f.always || f.value);

    return (
        <Modal title={`Room ${room.roomNumber} — Guest Details`} onClose={onClose}>
            <div className="modal-body-modern guest-details-modal">
                <div className="gd-hero">
                    <div className="guest-avatar lg">{(g.name || '?').charAt(0).toUpperCase()}</div>
                    <div className="gd-hero-text">
                        <div className="gd-name">{[g.title, g.name].filter(Boolean).join(' ')}</div>
                        <div className="gd-tags">
                            <span className="room-status-pill small" style={{ background: s.bg, color: s.color }}>{s.label}</span>
                            {sourceBadge && (
                                <span className={`src-badge ${sourceBadge.className}`} title={sourceBadge.title}>
                                    <i className="fas fa-plug"></i> {sourceBadge.label}
                                </span>
                            )}
                            {g.vip && <span className="gd-tag vip"><i className="fas fa-crown"></i> VIP</span>}
                            {g.sharer && <span className="gd-tag">Sharer</span>}
                        </div>
                    </div>
                </div>

                <div className="gd-grid">
                    {fields.map(f => (
                        <div className="gd-field" key={f.label}>
                            <div className="gd-field-label"><i className={`fas ${f.icon}`}></i> {f.label}</div>
                            <div className="gd-field-value">{f.value || '—'}</div>
                        </div>
                    ))}
                </div>

                {g.additionalGuests?.length > 0 && (
                    <>
                        <div className="gd-section-title">
                            Additional guests ({g.additionalGuests.length})
                            <span>each can log in to WiFi with the room number and their own last name</span>
                        </div>
                        <ul className="gd-guest-list">
                            {g.additionalGuests.map(name => (
                                <li key={name}>
                                    <span><i className="fas fa-user"></i> {name}</span>
                                    <span className="gd-origin">{fromPms.has(name.trim().toLowerCase()) ? 'From PMS' : 'Added at reception'}</span>
                                </li>
                            ))}
                        </ul>
                    </>
                )}

                {attributes.length > 0 && (
                    <>
                        <div className="gd-section-title">PMS attributes</div>
                        <div className="gd-attributes">
                            {attributes.map(([k, v]) => <span key={k} className="gd-tag">{k}: {v}</span>)}
                        </div>
                    </>
                )}
            </div>
            <div className="modal-footer-modern gd-actions">
                <button type="button" className="btn-secondary-modern" onClick={() => onAudit(room)}>
                    <i className="fas fa-history"></i> Audit History
                </button>
                <button type="button" className="btn-secondary-modern" onClick={() => onExtend(room)}>
                    <i className="fas fa-clock"></i> Extend Stay
                </button>
                <button type="button" className="btn-secondary-modern" onClick={() => onEditGuest(room)}>
                    <i className="fas fa-user-edit"></i> Edit Guest
                </button>
                <button type="button" className="btn-primary-modern" onClick={() => onCheckOut(room)}
                    style={{ background: 'linear-gradient(135deg, #f59e0b, #d97706)' }}>
                    <i className="fas fa-sign-out-alt"></i> Check Out
                </button>
            </div>
        </Modal>
    );
}

/* ── List view ─────────────────────────────────────────────────────────────── */
function RoomListHeader({ pmsEnabled }) {
    return (
        <div className="room-list-header">
            <span>Room</span>
            <span>Status</span>
            <span>Guest</span>
            <span className="col-checkout">Check-out</span>
            {pmsEnabled && <span className="col-source">Source</span>}
            <span className="text-right">Actions</span>
        </div>
    );
}

function RoomListRow({ room, pmsEnabled, onEdit, onCheckIn, onEditGuest, onCheckOut, onDelete, onExtend, onAudit, onShowGuest }) {
    const s = STATUS_CONFIG[room.status] || STATUS_CONFIG.available;
    const guest = room.currentGuest;
    const checkOut = formatDate(guest?.checkOutDate);
    const sourceBadge = getSourceBadge(room);
    const hasGuest = room.status === 'occupied' && !!guest?.name;

    return (
        <div className="room-list-row">
            <span className="room-number-text" title={room.roomName || undefined}><strong>{room.roomNumber}</strong>{room.floor ? ` · F${room.floor}` : ''}</span>
            <span><span className="room-status-pill small" style={{ background: s.bg, color: s.color }}>{s.label}</span></span>
            <span className="guest-name-text">
                {hasGuest
                    ? <button type="button" className="guest-name-link" onClick={() => onShowGuest(room)} title="Guest details">{guest.name}</button>
                    : (guest?.name || <em style={{ color: '#94a3b8' }}>—</em>)}
            </span>
            <span className="col-checkout">{checkOut}</span>
            {pmsEnabled && (
                <span className="col-source">
                    {sourceBadge
                        ? <span className={`src-badge ${sourceBadge.className}`} title={sourceBadge.title}><i className="fas fa-plug"></i> {sourceBadge.label}</span>
                        : <span style={{ color: '#cbd5e1', fontSize: '0.75rem' }}>—</span>
                    }
                </span>
            )}
            <span className="room-row-actions">
                {room.status === 'available' && <button className="room-action-btn checkin sm" onClick={() => onCheckIn(room)}><i className="fas fa-sign-in-alt"></i></button>}
                {room.status === 'occupied'  && <button className="room-action-btn checkout sm" onClick={() => onCheckOut(room)}><i className="fas fa-sign-out-alt"></i></button>}
                <RoomMoreMenu room={room} onEditGuest={onEditGuest} onExtend={onExtend} onAudit={onAudit} onEdit={onEdit} onDelete={onDelete} />
            </span>
        </div>
    );
}

/* ── CSV import: the format, and a file read against it (ROOM_TYPES: top of file) ── */
const ROOM_CSV_COLUMNS = [
    { key: 'roomNumber', required: true,  values: 'Text, unique in the branch', example: '101' },
    { key: 'roomName',   required: false, values: 'Text',                         example: 'Sea View' },
    { key: 'roomType',   required: false, values: ROOM_TYPES.join(' / '),         example: 'deluxe' },
    { key: 'floor',      required: false, values: 'Number',                       example: '1' },
    { key: 'building',   required: false, values: 'Text',                         example: 'Main' },
    { key: 'baseRate',   required: false, values: 'Number (per night)',           example: '2500' },
    { key: 'notes',      required: false, values: 'Text',                         example: 'Near lift' },
];
const ROOM_CSV_SAMPLE = 'roomNumber,roomName,roomType,floor,building,baseRate,notes\n'
    + '101,Sea View,deluxe,1,Main,2500,Near lift\n'
    + '102,,standard,1,Main,1800,\n'
    + '201,Family Suite,suite,2,Annex,4200,"Sofa bed, balcony"\n';
// Column names as people write them → the field they mean.
const ROOM_CSV_ALIASES = {
    roomnumber: 'roomNumber', room: 'roomNumber', roomno: 'roomNumber', number: 'roomNumber',
    roomname: 'roomName', name: 'roomName',
    roomtype: 'roomType', type: 'roomType',
    floor: 'floor', building: 'building', block: 'building',
    baserate: 'baseRate', rate: 'baseRate', price: 'baseRate',
    notes: 'notes', note: 'notes', remarks: 'notes',
};

/** RFC-4180-ish: quoted fields may hold commas, quotes ("") and line breaks. */
function parseCsvText(text) {
    const rows = [];
    let row = [];
    let field = '';
    let quoted = false;
    for (let i = 0; i < text.length; i++) {
        const c = text[i];
        if (quoted) {
            if (c === '"') {
                if (text[i + 1] === '"') { field += '"'; i++; } else quoted = false;
            } else field += c;
        } else if (c === '"') quoted = true;
        else if (c === ',') { row.push(field); field = ''; }
        else if (c === '\n' || c === '\r') {
            if (c === '\r' && text[i + 1] === '\n') i++;
            row.push(field); rows.push(row); row = []; field = '';
        } else field += c;
    }
    if (field !== '' || row.length) { row.push(field); rows.push(row); }
    return rows.filter(r => r.some(v => v.trim() !== ''));
}

/** The file's rows as room records, each with what is wrong with it (if anything). */
function parseRoomsCsv(text) {
    const rows = parseCsvText(text.replace(/^﻿/, ''));
    if (rows.length < 2) throw new Error('The CSV needs a header row and at least one room row');
    const fields = rows[0].map(h => ROOM_CSV_ALIASES[h.trim().toLowerCase().replace(/[\s_-]/g, '')] || null);
    if (!fields.includes('roomNumber')) throw new Error('No "roomNumber" column found in the header row');
    const seen = new Set();
    return rows.slice(1).map(values => {
        const data = {};
        const errors = [];
        fields.forEach((f, i) => { if (f && values[i] !== undefined && values[i].trim() !== '') data[f] = values[i].trim(); });
        if (!data.roomNumber) errors.push('roomNumber is empty');
        else if (seen.has(data.roomNumber)) errors.push('roomNumber repeated in the file');
        seen.add(data.roomNumber);
        if (data.roomType) {
            data.roomType = data.roomType.toLowerCase();
            if (!ROOM_TYPES.includes(data.roomType)) errors.push(`roomType must be one of ${ROOM_TYPES.join(', ')}`);
        }
        for (const key of ['floor', 'baseRate']) {
            if (data[key] === undefined) continue;
            const n = Number(data[key]);
            if (Number.isFinite(n)) data[key] = n; else errors.push(`${key} is not a number`);
        }
        return { data, errors };
    });
}

/* ── Modal wrapper ─────────────────────────────────────────────────────────── */
function Modal({ title, children, onClose, small, danger }) {
    return (
        <div className="modal-overlay-modern" onClick={onClose}>
            <div className={`modal-modern ${small ? 'modal-small' : ''}`} onClick={e => e.stopPropagation()}>
                <div className={`modal-header-modern ${danger ? 'danger' : ''}`}>
                    <div><h2>{title}</h2></div>
                    <button className="modal-close" onClick={onClose}><i className="fas fa-times"></i></button>
                </div>
                {children}
            </div>
        </div>
    );
}
